import { defineStore } from 'pinia';
import { apiCall } from '@/utils/globalFunctions';
import store from '@/store/store';

// Helper functions for persistence
function getPersistedState(storageKey) {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to read localStorage for subscriptionStore', error);
    return {};
  }
}

function setPersistedState(storageKey, state) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to persist subscription state', error);
  }
}

export const useSubscriptionStore = defineStore('subscription', {
  state: () => {
    const userId = store.state.user?.user_id || null;
    const storageKey = userId ? `subscriptionStore_${userId}` : 'subscriptionStore';
    const persisted = getPersistedState(storageKey);
    return {
      userId,
      plans: persisted.plans || [],
      currentSubscription: persisted.currentSubscription || null,
      paymentHistory: persisted.paymentHistory || [],
      usage: persisted.usage || { invoice_count: 0, email_count: 0, daily_email_count: 0, device_count: 0, company_count: 0, client_count: 0 },
      limits: persisted.limits || { monthly_invoices: 5, monthly_emails: 10, daily_emails: 5, allowed_device: 1, allowed_company: 1, allowed_client: 5 },
      loadingPlans: false,
      loadingSubscription: false,
      loadingAction: false,
      error: null,
      lastErrorCode: null,
    };
  },

  getters: {
    activePlans: (state) => state.plans.filter((plan) => plan.is_active),
    hasActiveSubscription: (state) =>
      state.currentSubscription && ['active', 'pending'].includes(state.currentSubscription?.status),
    isFreePlan: (state) =>
      !state.currentSubscription ||
      state.currentSubscription.isFreePlan ||
      state.currentSubscription.plan_id === 1 ||
      state.currentSubscription.price === 0 ||
      state.currentSubscription.status === 'expired' ||
      (state.currentSubscription.end_date && new Date(state.currentSubscription.end_date) < new Date()),
    isCanceledEligible: (state) =>
      state.currentSubscription?.status === 'canceled',
    isExpired: (state) =>
      state.currentSubscription?.status === 'expired' ||
      (state.currentSubscription?.end_date && new Date(state.currentSubscription.end_date) < new Date()),
    isPendingVerification: (state) =>
      state.currentSubscription?.status === 'pending_verification',
    upcomingPayment: (state) => state.paymentHistory[0] || null,
    loading: (state) => state.loadingPlans || state.loadingSubscription || state.loadingAction,
    canCreateInvoice: (state) =>
      state.limits.monthly_invoices === null || state.usage.invoice_count < state.limits.monthly_invoices,
    canSendEmail: (state) =>
      (state.limits.monthly_emails === null || state.usage.email_count < state.limits.monthly_emails) &&
      (state.limits.daily_emails === null || state.usage.daily_email_count < state.limits.daily_emails),
    invoiceProgress: (state) =>
      state.limits.monthly_invoices > 0
        ? (state.usage.invoice_count / state.limits.monthly_invoices) * 100
        : 0,
    canCreateCompany: (state) =>
      state.limits.allowed_company === null || state.usage.company_count < state.limits.allowed_company,
    canCreateClient: (state) =>
      state.limits.allowed_client === null || state.usage.client_count < state.limits.allowed_client,
    canCreateDevice: (state) =>
      state.limits.allowed_device === null || state.usage.device_count < state.limits.allowed_device,
    emailProgress: (state) =>
      state.limits.monthly_emails > 0
        ? (state.usage.email_count / state.limits.monthly_emails) * 100
        : 0,
    dailyEmailProgress: (state) =>
      state.limits.daily_emails > 0
        ? (state.usage.daily_email_count / state.limits.daily_emails) * 100
        : 0,
  },

  actions: {
    persistState() {
      if (!this.userId) return;
      
      // Use nextTick to ensure state is updated
      //this.$nextTick(() => {
        setPersistedState(`subscriptionStore_${this.userId}`, {
          plans: this.plans,
          currentSubscription: this.currentSubscription,
          paymentHistory: this.paymentHistory,
          usage: this.usage,
          limits: this.limits,
        });
      //});
    },

    setUserId(userId) {
      this.userId = userId;
      this.reset();
      if (userId) {
        this.fetchPlans();
        this.fetchUserSubscription();
      }
    },

    reset() {
      this.$reset();
      if (this.userId) {
        localStorage.removeItem(`subscriptionStore_${this.userId}`);
      }
    },

    // subscription.js
    async changePlan(planId) {
      if (!this.userId) {
        this.error = 'No user logged in';
        this.lastErrorCode = 'NO_USER';
        throw new Error('No user logged in');
      }
      
      this.loadingAction = true;
      this.error = null;
      
      try {
        const res = await apiCall(
          {
            method: 'POST',
            url: '/Subscription/changePlan',
            data: { plan_id: planId },
            dataType: 'json',
          },
          store
        );

        await this.fetchUserSubscription();
        this.persistState();
        return res.data;
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'Failed to change plan';
        this.lastErrorCode = err.response?.status || 'CHANGE_PLAN_FAILED';
        throw err;
      } finally {
        this.loadingAction = false;
      }
    },

    async fetchPlans() {
      if (!this.userId) {
        this.error = 'No user logged in';
        this.lastErrorCode = 'NO_USER';
        console.warn('fetchPlans: No userId');
        return;
      }
      this.loadingPlans = true;
      this.error = null;
      try {
        const res = await Promise.race([
          apiCall(
            {
              method: 'GET',
              url: '/Subscription/getPlans',
              dataType: 'json',
            },
            store
          ),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), 10000))
        ]);

        this.plans = res.data.map((plan) => ({
          ...plan,
          features: Array.isArray(plan.features) ? plan.features : JSON.parse(plan.features || '{}'),
        }));
        this.persistState();
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'Failed to fetch plans';
        this.lastErrorCode = err.response?.status || err.message === 'Request timeout' ? 'TIMEOUT' : 'FETCH_PLANS_FAILED';
        console.error('fetchPlans error:', { error: this.error, code: this.lastErrorCode, url: '/Subscription/getPlans' });
        throw err;
      } finally {
        this.loadingPlans = false;
      }
    },

    async fetchUserSubscription() {
      if (!this.userId) {
        this.error = 'No user logged in';
        this.lastErrorCode = 'NO_USER';
        console.warn('fetchUserSubscription: No userId');
        return;
      }
      this.loadingSubscription = true;
      this.error = null;
      try {
        const res = await Promise.race([
          apiCall(
            {
              method: 'GET',
              url: '/Subscription/getUserSubscription',
              dataType: 'json',
            },
            store
          ),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), 10000))
        ]);
        
        this.currentSubscription = res.data.subscription;
        this.paymentHistory = res.data.payments || [];

        this.persistState();

        this.fetchUsage();

        // Notify user if on free plan due to expiration
        if (this.isExpired && this.currentSubscription?.plan_id !== 1) {
          this.limits = { monthly_invoices: 5, monthly_emails: 10, daily_emails: 5, allowed_device: 1, allowed_company: 1, allowed_client: 5 };
          this.error = 'Your subscription has expired. You are now on the free plan with limited features.';
          this.lastErrorCode = 'SUBSCRIPTION_EXPIRED';
        }
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'Failed to fetch subscription';
        this.lastErrorCode = err.response?.status || err.message === 'Request timeout' ? 'TIMEOUT' : 'FETCH_SUBSCRIPTION_FAILED';
        this.limits = { monthly_invoices: 5, monthly_emails: 10, daily_emails: 5, allowed_device: 1, allowed_company: 1, allowed_client: 5 };
        console.error('fetchUserSubscription error:', { error: this.error, code: this.lastErrorCode });
      } finally {
        this.loadingSubscription = false;
      }
    },

    async fetchUsage() {
      if (!this.userId) {
        this.error = 'No user logged in';
        this.lastErrorCode = 'NO_USER';
        return;
      }

      this.loadingAction = true;
      this.error = null;

      try {
        const res = await apiCall(
          {
            method: 'GET',
            url: '/Usage/getUsage',
            data: { user_id: this.userId }
          },
          store
        );

        this.usage = {
          invoice_count: res.data.invoice_count || 0,
          email_count: res.data.email_count || 0,
          daily_email_count: res.data.daily_email_count || 0,
          device_count: res.data.device_count || 0,
          company_count: res.data.company_count || 0,
          client_count: res.data.client_count || 0
        };
        this.limits = {
          monthly_invoices: res.data.monthly_invoices || 5,
          monthly_emails: res.data.monthly_emails || 10,
          daily_emails: res.data.daily_emails || 5,
          allowed_device: res.data.allowed_device || 1,
          allowed_company: res.data.allowed_company || 1,
          allowed_client: res.data.allowed_client || 5
        };

        this.persistState();
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'Failed to fetch usage';
        this.lastErrorCode = err.response?.status || 'FETCH_USAGE_FAILED';
        console.error('fetchUsage error:', { error: this.error, code: this.lastErrorCode });
      } finally {
        this.loadingAction = false;
      }
    },

    async incrementInvoiceCount() {
      try {
        const res = await apiCall({
          method: 'POST',
          url: '/Usage/incrementInvoiceCount',
          data: { user_id: this.userId }
        }, store);
        await this.fetchUsage(); // Refresh usage after increment
        return res.data;
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to increment invoice count';
        this.lastErrorCode = err.response?.status || 'INCREMENT_INVOICE_FAILED';
        throw err;
      }
    },

    /*async checkSubscriptionStatus() {
      if (!this.userId) {
        this.error = 'No user logged in';
        this.lastErrorCode = 'NO_USER';
        console.warn('checkSubscriptionStatus: No userId');
        return false;
      }

      if (this.currentSubscription && !this.isExpired && this.hasActiveSubscription) {
        return !this.isFreePlan;
      }

      this.loadingSubscription = true;
      try {
        const res = await Promise.race([
          apiCall(
            {
              method: 'GET',
              url: '/Subscription/getUserSubscription',
              dataType: 'json',
            },
            store
          ),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), 10000))
        ]);

        this.currentSubscription = res.data.subscription;
        this.paymentHistory = res.data.payments || [];
        this.usage = res.data.usage || { invoice_count: 0, email_count: 0, daily_email_count: 0 };
        this.limits = res.data.subscription?.features || { monthly_invoices: 5, monthly_emails: 10, daily_emails: 5 };
        this.persistState();

        if (this.isExpired && !this.currentSubscription?.plan_id === 1) {
          this.limits = { monthly_invoices: 5, monthly_emails: 10, daily_emails: 5 };
          this.error = 'Your subscription has expired. You are now on the free plan with limited features.';
          this.lastErrorCode = 'SUBSCRIPTION_EXPIRED';
        }

        console.debug('checkSubscriptionStatus success:', { isFreePlan: this.isFreePlan, hasActiveSubscription: this.hasActiveSubscription });
        return !this.isFreePlan && this.hasActiveSubscription;
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'Error checking subscription status';
        this.lastErrorCode = err.response?.status || err.message === 'Request timeout' ? 'TIMEOUT' : 'CHECK_SUBSCRIPTION_FAILED';
        this.limits = { monthly_invoices: 5, monthly_emails: 10, daily_emails: 5 };
        console.error('checkSubscriptionStatus error:', { error: this.error, code: this.lastErrorCode });
        return false;
      } finally {
        this.loadingSubscription = false;
      }
    },*/

    async subscribe(planId, paymentMethod, paymentData) {
      if (!this.userId) {
        this.error = 'No user logged in';
        this.lastErrorCode = 'NO_USER';
        throw new Error('No user logged in');
      }
      if (this.isPendingVerification) {
        this.error = 'You have a pending crypto payment. Please wait for verification before proceeding.';
        this.lastErrorCode = 'PENDING_CRYPTO_PAYMENT';
        throw new Error(this.error);
      }
      this.loadingAction = true;
      this.error = null;
      try {
        const data = {
          plan_id: planId,
          payment_method: paymentMethod,
        };
        if (paymentMethod === 'paypal') {
          data.orderId = paymentData.orderId;
        } else if (paymentMethod === 'crypto') {
          data.crypto_currency = paymentData.crypto_currency;
          data.transaction_details = paymentData.transaction_details;
          data.receipt = paymentData.receipt;
        }
        const res = await apiCall(
          {
            method: 'POST',
            url: '/Subscription/subscribe',
            data,
            dataType: paymentMethod === 'crypto' ? 'multipart/form-data' : 'json',
          },
          store
        );

        await this.fetchUserSubscription();
        this.persistState();
        console.debug('subscribe success:', res.data);
        return res.data;
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'Subscription failed';
        this.lastErrorCode = err.response?.status || 'SUBSCRIBE_FAILED';
        console.error('subscribe error:', { error: this.error, code: this.lastErrorCode });
        throw err;
      } finally {
        this.loadingAction = false;
      }
    },

    async cancelSubscription() {
      if (!this.userId) {
        this.error = 'No user logged in';
        this.lastErrorCode = 'NO_USER';
        throw new Error('No user logged in');
      }
      this.loadingAction = true;
      this.error = null;
      try {
        const res = await apiCall(
          {
            method: 'POST',
            url: '/Subscription/cancelSubscription',
            dataType: 'json',
          },
          store
        );

        await this.fetchUserSubscription();
        this.persistState();
        console.debug('cancelSubscription success:', res.data);
        return res;
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'Failed to cancel subscription';
        this.lastErrorCode = err.response?.status || 'CANCEL_SUBSCRIPTION_FAILED';
        console.error('cancelSubscription error:', { error: this.error, code: this.lastErrorCode });
        throw err;
      } finally {
        this.loadingAction = false;
      }
    },

    async reactivateSubscription() {
      if (!this.userId) {
        this.error = 'No user logged in';
        this.lastErrorCode = 'NO_USER';
        throw new Error('No user logged in');
      }
      this.loadingAction = true;
      this.error = null;
      try {
        const res = await apiCall(
          {
            method: 'POST',
            url: '/Subscription/reactivateSubscription',
            dataType: 'json',
          },
          store
        );

        await this.fetchUserSubscription();
        this.persistState();
        console.debug('reactivateSubscription success:', res.data);
        return res;
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'Failed to reactivate subscription';
        this.lastErrorCode = err.response?.status || 'REACTIVATE_SUBSCRIPTION_FAILED';
        console.error('reactivateSubscription error:', { error: this.error, code: this.lastErrorCode });
        throw err;
      } finally {
        this.loadingAction = false;
      }
    },

    async renewSubscription(planId, paymentMethod, paymentData) {
      if (!this.userId) {
        this.error = 'No user logged in';
        this.lastErrorCode = 'NO_USER';
        throw new Error('No user logged in');
      }
      if (this.isPendingVerification) {
        this.error = 'You have a pending crypto payment. Please wait for verification before proceeding.';
        this.lastErrorCode = 'PENDING_CRYPTO_PAYMENT';
        throw new Error(this.error);
      }
      this.loadingAction = true;
      this.error = null;
      try {
        const data = {
          plan_id: planId,
          payment_method: paymentMethod,
        };
        if (paymentMethod === 'paypal') {
          data.orderId = paymentData.orderId;
        } else if (paymentMethod === 'crypto') {
          data.crypto_currency = paymentData.crypto_currency;
          data.transaction_details = paymentData.transaction_details;
          data.receipt = paymentData.receipt;
        }
        const res = await apiCall(
          {
            method: 'POST',
            url: '/Subscription/renewSubscription',
            data,
            dataType: paymentMethod === 'crypto' ? 'multipart/form-data' : 'json',
          },
          store
        );

        await this.fetchUserSubscription();
        this.persistState();
        console.debug('renewSubscription success:', res.data);
        return res.data;
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'Failed to renew subscription';
        this.lastErrorCode = err.response?.status || 'RENEW_SUBSCRIPTION_FAILED';
        console.error('renewSubscription error:', { error: this.error, code: this.lastErrorCode });
        throw err;
      } finally {
        this.loadingAction = false;
      }
    },

    async updatePaymentMethod(paymentMethod, paymentData) {
      if (!this.userId) {
        this.error = 'No user logged in';
        this.lastErrorCode = 'NO_USER';
        throw new Error('No user logged in');
      }
      if (this.isPendingVerification) {
        this.error = 'You have a pending crypto payment. Please wait for verification before proceeding.';
        this.lastErrorCode = 'PENDING_CRYPTO_PAYMENT';
        throw new Error(this.error);
      }
      this.loadingAction = true;
      this.error = null;
      try {
        const data = {
          payment_method: paymentMethod,
        };
        if (paymentMethod === 'paypal') {
          data.payment_token = paymentData.payment_token;
        } else if (paymentMethod === 'crypto') {
          data.crypto_currency = paymentData.crypto_currency;
          data.transaction_details = paymentData.transaction_details;
          data.receipt = paymentData.receipt;
        }
        const res = await apiCall(
          {
            method: 'POST',
            url: '/Subscription/updatePaymentMethod',
            data,
            dataType: paymentMethod === 'crypto' ? 'multipart/form-data' : 'json',
          },
          store
        );

        await this.fetchUserSubscription();
        this.persistState();
        console.debug('updatePaymentMethod success:', res.data);
        return res;
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'Failed to update payment method';
        this.lastErrorCode = err.response?.status || 'UPDATE_PAYMENT_METHOD_FAILED';
        console.error('updatePaymentMethod error:', { error: this.error, code: this.lastErrorCode });
        throw err;
      } finally {
        this.loadingAction = false;
      }
    },

    async createPaypalOrder(planId, amount, currency) {
      if (!this.userId) {
        this.error = 'No user logged in';
        this.lastErrorCode = 'NO_USER';
        throw new Error('No user logged in');
      }
      if (this.isPendingVerification) {
        this.error = 'You have a pending crypto payment. Please wait for verification before proceeding.';
        this.lastErrorCode = 'PENDING_CRYPTO_PAYMENT';
        throw new Error(this.error);
      }
      this.loadingAction = true;
      this.error = null;
      try {
        const res = await apiCall(
          {
            method: 'POST',
            url: '/Subscription/createPaypalOrder',
            data: {
              plan_id: planId,
              amount,
              currency,
            },
            dataType: 'json',
          },
          store
        );

        console.debug('createPaypalOrder success:', res.data);
        return res.data.orderId;
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'Failed to create PayPal order';
        this.lastErrorCode = err.response?.status || 'CREATE_PAYPAL_ORDER_FAILED';
        console.error('createPaypalOrder error:', { error: this.error, code: this.lastErrorCode });
        throw err;
      } finally {
        this.loadingAction = false;
      }
    },

    async capturePaypalOrder(orderId) {
      if (!this.userId) {
        this.error = 'No user logged in';
        this.lastErrorCode = 'NO_USER';
        throw new Error('No user logged in');
      }
      if (this.isPendingVerification) {
        this.error = 'You have a pending crypto payment. Please wait for verification before proceeding.';
        this.lastErrorCode = 'PENDING_CRYPTO_PAYMENT';
        throw new Error(this.error);
      }
      this.loadingAction = true;
      this.error = null;
      try {
        const res = await apiCall(
          {
            method: 'POST',
            url: '/Subscription/capturePaypalOrder',
            data: { orderId },
            dataType: 'json',
          },
          store
        );

        console.debug('capturePaypalOrder success:', res.data);
        return res.data;
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'Failed to capture PayPal order';
        this.lastErrorCode = err.response?.status || 'CAPTURE_PAYPAL_ORDER_FAILED';
        console.error('capturePaypalOrder error:', { error: this.error, code: this.lastErrorCode });
        throw err;
      } finally {
        this.loadingAction = false;
      }
    },

    async getBinancePayLink(amountUsdt) {
      if (!this.userId) {
        this.error = 'No user logged in';
        this.lastErrorCode = 'NO_USER';
        throw new Error('No user logged in');
      }
      if (this.isPendingVerification) {
        this.error = 'You have a pending crypto payment. Please wait for verification before proceeding.';
        this.lastErrorCode = 'PENDING_CRYPTO_PAYMENT';
        throw new Error(this.error);
      }
      this.loadingAction = true;
      this.error = null;
      try {
        const res = await apiCall(
          {
            method: 'GET',
            url: '/Subscription/getBinancePayLink',
            data: {
              amount_usdt: amountUsdt,
            },
            dataType: 'json',
          },
          store
        );

        return res.data;
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'Failed to fetch Binance Pay link';
        this.lastErrorCode = err.response?.status || 'FETCH_BINANCE_LINK_FAILED';
        console.error('getBinancePayLink error:', { error: this.error, code: this.lastErrorCode });
        throw err;
      } finally {
        this.loadingAction = false;
      }
    },

    clearError() {
      this.error = null;
      this.lastErrorCode = null;
    },
  },
});